import { useState } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader } from '../components/Layout.jsx';
import { Card, Empty, ErrorState, Input, Loading, PaymentBadge, Select, Stat } from '../components/ui.jsx';
import { ConfirmModal } from '../components/Modal.jsx';
import { IconPlus } from '../components/Icons.jsx';
import { api } from '../lib/api.js';
import { useApi, useDebounced } from '../lib/hooks.js';
import { useAuth } from '../lib/auth.jsx';
import { useToast } from '../lib/toast.jsx';
import { t } from '../lib/i18n.js';
import { daysAgo, formatDate, liters, money, moneyPair, num, quantityLabel, today } from '../lib/format.js';

export default function SaleList() {
  const { isAdmin } = useAuth();
  const toast = useToast();
  const [from, setFrom] = useState(daysAgo(29));
  const [to, setTo] = useState(today());
  const [customerId, setCustomerId] = useState('');
  const [purchaseId, setPurchaseId] = useState('');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounced(search);
  const [deleting, setDeleting] = useState(null);
  const [busy, setBusy] = useState(false);

  const customers = useApi('/api/customers', { include_inactive: true });
  const purchases = useApi('/api/purchases', { limit: 500 });
  const { data, error, loading, reload } = useApi('/api/sales', {
    from,
    to,
    customer_id: customerId,
    purchase_id: purchaseId,
    q: debouncedSearch,
    limit: 200,
  });

  const remove = async () => {
    setBusy(true);
    try {
      await api.del(`/api/sales/${deleting.id}`);
      toast.success(t.sale.deleted);
      setDeleting(null);
      reload();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHeader
        title={t.sale.title}
        subtitle={t.sale.subtitle}
        actions={
          <Link to="/sell/new" className="btn btn-sm btn-primary no-print">
            <IconPlus /> {t.common.add}
          </Link>
        }
      />

      <div className="page">
        <Card>
          <div className="filters">
            <Input label={t.common.from} type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            <Input label={t.common.to} type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            <Select
              label={t.sale.customer}
              placeholder={t.party.allCustomers}
              value={customerId}
              onChange={(e) => setCustomerId(e.target.value)}
              options={(customers.data || []).map((c) => ({ value: c.id, label: c.name }))}
            />
            <Select
              label={t.sale.batch}
              placeholder={t.sale.allBatches}
              value={purchaseId}
              onChange={(e) => setPurchaseId(e.target.value)}
              options={[
                { value: '0', label: t.sale.noBatch },
                ...(purchases.data?.rows || []).map((p) => ({ value: p.id, label: p.batch_no })),
              ]}
            />
            <Input
              className="grow"
              label={t.common.search}
              placeholder={t.sale.searchHint}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </Card>

        {data?.totals && (
          <div className="stat-grid">
            <Stat label={t.sale.sales} value={num(data.totals.count)} />
            <Stat label={t.sale.volume} value={liters(data.totals.total_liters)} />
            <Stat
              label={t.sale.revenue}
              value={moneyPair(data.totals.total_iqd, data.totals.total_usd, { zero: money(0, 'IQD') })}
            />
          </div>
        )}

        <ErrorState error={error} onRetry={reload} />
        {loading && !data && <Loading />}

        {data && data.rows.length === 0 && (
          <Card>
            <Empty
              title={t.sale.none}
              message={t.sale.noneHint}
              action={
                <Link to="/sell/new" className="btn btn-primary">
                  <IconPlus /> {t.sale.record}
                </Link>
              }
            />
          </Card>
        )}

        {data && data.rows.length > 0 && (
          <div className="record-list">
            {data.rows.map((row) => (
              <div key={row.id} className="record">
                <Link to={`/sell/${row.id}/receipt`}>
                  <div className="record-top">
                    <span className="title">{row.customer_name}</span>
                    <span className="amount">
                      {money(row.currency === 'IQD' ? row.amount_iqd : row.amount_usd, row.currency)}
                    </span>
                  </div>
                  <div className="record-meta">
                    <span className="strong">{row.receipt_no}</span>
                    <span>{formatDate(row.sold_on)}</span>
                    <span>
                      {quantityLabel(row.unit, row.quantity)} · {liters(row.liters)}
                    </span>
                    <span>{row.tank_name}</span>
                    {row.purchase_batch_no && <span className="badge muted">{row.purchase_batch_no}</span>}
                    <PaymentBadge type={row.payment_type} />
                  </div>
                </Link>
                {isAdmin && (
                  <div className="btn-row no-print" style={{ marginTop: 4 }}>
                    <button type="button" className="btn btn-sm btn-danger" onClick={() => setDeleting(row)}>
                      {t.common.delete}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <Link to="/sell/new" className="btn btn-primary fab no-print">
        <IconPlus /> {t.sale.newTitle}
      </Link>

      {deleting && (
        <ConfirmModal
          title={t.sale.deleteTitle}
          message={t.sale.deleteMessage(deleting.receipt_no)}
          onConfirm={remove}
          onClose={() => setDeleting(null)}
          busy={busy}
        />
      )}
    </>
  );
}
